<template>
  <CollapsibleSection
    v-if="shouldShowSection"
    title="Disponibilités du client"
    icon="fas fa-calendar-alt"
    context="quote"
    storage-key="disponibilites-info"
    :use-grid="false"
    :bare="bare"
  >
    <div class="availability-section">
      <!-- En édition, le même choix en trois cartes que dans la demande de devis
           du client (AvailabilityChoice) : jours précis, période en mois, ou
           « je ne connais pas encore les dates ». En lecture, le calendrier. -->
      <DetailEmpty
        v-if="!isEditing && !hasSelectedDates && !hasSelectedPeriods && !currentDatesInconnues"
        tag="p"
        text="Aucune date sélectionnée"
      />
      <AvailabilityChoice
        v-else-if="isEditing"
        :dates="displayDates"
        :periods="currentPeriodes"
        :unknown="currentDatesInconnues"
        @update:dates="emit('update:dates-disponibilites', $event)"
        @update:periods="emit('update:dates-periodes', $event)"
        @update:unknown="emit('update:dates-inconnues', $event)"
      />
      <AvailabilityCalendar
        v-else
        :model-value="displayDates"
        :periods="currentPeriodes"
        :dates-inconnues="currentDatesInconnues"
        readonly
        :allow-periods="allowPeriods"
      />
      <InlineNotice
        v-if="!isEditing && currentDatesInconnues"
        variant="warning"
        size="md"
        style="margin-top: 1rem;"
      >
        Le client ne connaît pas encore les dates de formation disponibles
      </InlineNotice>
      <!-- Quand on intervient, après quels jours : les horaires de la
           formation (retour Figma du 2026-09-11). Ils étaient dans « Lieu de
           la formation », absente pour une formation INTER. -->
      <QuoteDetailHoraires :quote="quote" :is-editing="isEditing" :edited-quote="editedQuote" />
    </div>
    <template #footer>
      <slot name="footer" />
    </template>
  </CollapsibleSection>
</template>

<script setup>
import { computed } from 'vue'
import { normalizePeriods } from '@shared/formation-periods.js'
import { CollapsibleSection, DetailEmpty } from '../data'
import { AvailabilityCalendar, AvailabilityChoice } from '../quote-form'
import InlineNotice from '../ui/InlineNotice.vue'
import QuoteDetailHoraires from './QuoteDetailHoraires.vue'

const props = defineProps({
  /**
   * Mode « nu » : le contenu seul, sans carte ni en-tête repliable. La fenêtre
   * « Analyse du besoin » v2 n'affiche qu'une section à la fois et porte
   * elle-même son titre — la carte y ferait un cadre dans un cadre.
   * Additif : sans la prop, le rendu est exactement celui d'avant.
   */
  bare: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  editedQuote: {
    type: Object,
    default: () => ({})
  },
  // Montre aussi les mois entiers (période approximative) dans la lecture. En
  // édition, le choix « une période en tête » est toujours proposé.
  allowPeriods: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:dates-disponibilites', 'update:dates-periodes', 'update:dates-inconnues'])

// Utiliser editedQuote en mode édition, sinon quote
const currentQuote = computed(() => {
  return props.isEditing ? props.editedQuote : props.quote
})

// Fonction pour normaliser les dates au format YYYY-MM-DD
const normalizeDates = (dates) => {
  if (!dates || !Array.isArray(dates)) {
    return []
  }
  return dates.map(date => {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date
    }
    if (date instanceof Date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    try {
      const dateObj = new Date(date)
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear()
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const day = String(dateObj.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
    } catch (e) {
      // Ignorer les erreurs
    }
    return date
  })
}

// Les trois saisies exclusives de la demande, lues sur la demande affichée
// (editedQuote en édition, quote sinon).
const displayDates = computed(() => normalizeDates(currentQuote.value?.dates_disponibilites))
const currentPeriodes = computed(() => normalizePeriods(currentQuote.value?.dates_periodes))
const currentDatesInconnues = computed(() => currentQuote.value?.dates_inconnues || false)

const hasSelectedPeriods = computed(() => currentPeriodes.value.length > 0)

const hasSelectedDates = computed(() => {
  const dates = currentQuote.value?.dates_disponibilites
  return dates && Array.isArray(dates) && dates.length > 0
})

const shouldShowSection = computed(() => {
  return true
})
</script>

<style scoped>
.availability-section {
  margin-top: 0;
}
</style>

